import { useForm } from 'react-hook-form';
import QuestionsForm from './QuestionsForm';
import CareMapprButton from '../../../components/Button/CareMapprButton';
import { useDataAnnotation } from '../context/DataAnnotationProvider';
import type {
  AddSurveyResponsePayload,
  AddSurveyResponsePayloadSidebar,
  QuestionAnswer,
  QuestionnaireTO,
  QuestionTO,
} from '../../../../lib';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../../../../store';
import {
  increaseResolvingSubmitCount,
  resetResolvingSubmitCount,
} from '../../../../store/ai2';
import { useLocation } from 'react-router-dom';
import { SystemPermission, useToast } from '../../../../context';
import useUserPermission from '../../../hooks/useUserPermission';
type SideBarType = 'default' | 'data-annotation-resolving';
interface QuestionsSidebarProps {
  inPatientDetails?: boolean;
  className?: string;
  isSideBar?: boolean;
  sideBarType?: SideBarType;
  onHidePopup?: () => void;
}

export type DynamicQuestionFormTypes = { [key: string]: any };

const getDefaultValue = (question: QuestionTO) => {
  if (question.type === 'radio') {
    return question.value && question.value?.length > 0
      ? question.value[0]
      : '';
  }
  return '';
};

export const generateDefaultValues = (questionnaires: QuestionnaireTO[]) => {
  return questionnaires.reduce((totalValues, questionnaire) => {
    const questionValues = questionnaire.questions.reduce((acc, question) => {
      acc[`${question.questionnairesId}-question-${question.id}`] =
        getDefaultValue(question);
      return acc;
    }, {} as DynamicQuestionFormTypes);

    return { ...totalValues, ...questionValues };
  }, {} as DynamicQuestionFormTypes);
};

const QuestionsSidebar = ({
  inPatientDetails,
  className,
  isSideBar = false,
  sideBarType = 'default',
  onHidePopup,
}: QuestionsSidebarProps) => {
  const { onSubmitResponse, currentCase, discrepency } = useDataAnnotation();
  const location = useLocation();
  const { selectedTenancy } = useSelector(
    (state: RootState) => state.clinicianProfile,
  );
  const permissionShouldBeChecked = discrepency
    ? SystemPermission.ADD_FEEDBACK_V2
    : SystemPermission.ADD_FEEDBACK;
  const { userHasPermissions } = useUserPermission();
  const sidebarQuestionnaire = selectedTenancy?.questionnaires?.find(
    (questionnaire) => questionnaire.title === 'AutoMedic Label-as-you-go',
  );
  const { currentQuestionnaire, resolvingSubmitCount } = useSelector(
    (state: RootState) => state.dataAnnotation,
  );
  const dispatch = useDispatch();
  const { setToast } = useToast();
  const questionsForm = useForm<DynamicQuestionFormTypes>({
    defaultValues:
      generateDefaultValues(selectedTenancy?.questionnaires || []) || {},
  });

  const thisQuestionnaire = isSideBar
    ? sidebarQuestionnaire
    : currentQuestionnaire;

  const onSubmit = async () => {
    if (!selectedTenancy || !thisQuestionnaire || !currentCase) return;
    setToast(
      <span>
        Submitting feedback <span className="dots" />
      </span>,
    );
    const formEntries = Object.entries(questionsForm.getValues()).filter(
      ([key]) => key.includes(`${thisQuestionnaire.id}-question`),
    );
    const formValues: QuestionAnswer[] = formEntries.map(([key, value]) => {
      const [, questionId] = key.split('-question-').map(Number);
      return { questionId, answers: value };
    });
    const payload: AddSurveyResponsePayload = {
      tenancyId: thisQuestionnaire.tenancyId,
      annotationCaseId: currentCase.caseId,
      status:
        sideBarType === 'default'
          ? 'Completed'
          : sideBarType === 'data-annotation-resolving' &&
              resolvingSubmitCount === 0
            ? 'In Review'
            : 'Completed',
      surveyId: thisQuestionnaire.id,
      questions: formValues,
    };
    await onSubmitResponse(payload);
    if (!discrepency || resolvingSubmitCount > 0) {
      questionsForm.reset();
    }
  };

  const onSubmitSideBar = async () => {
    if (!selectedTenancy || !thisQuestionnaire) return;
    setToast(
      <span>
        Submitting feedback <span className="dots" />
      </span>,
    );
    const formEntries = Object.entries(questionsForm.getValues()).filter(
      ([key]) => key.includes(`${thisQuestionnaire.id}-question`),
    );
    const formValues: QuestionAnswer[] = formEntries.map(([key, value]) => {
      const [, questionId] = key.split('-question-').map(Number);
      return { questionId, answers: value };
    });
    const searchParams = new URLSearchParams(location.search);
    const access_token = searchParams.get('access_token');
    const payload: AddSurveyResponsePayloadSidebar = {
      tenancyId: thisQuestionnaire.tenancyId,
      status:
        sideBarType === 'default'
          ? 'Completed'
          : sideBarType === 'data-annotation-resolving' &&
              resolvingSubmitCount === 0
            ? 'In Review'
            : 'Completed',
      surveyId: thisQuestionnaire.id,
      questions: formValues,
      access_token: access_token || '',
    };
    setToast('Submitting feedback...');
    await onSubmitResponse(payload);
    setToast('Feedback submitted successfully.');
    if (!discrepency || resolvingSubmitCount > 0) {
      questionsForm.reset();
    }
    onHidePopup && onHidePopup();
  };

  const onSubmitDescrepency = async () => {
    if (!selectedTenancy || !thisQuestionnaire || !currentCase) return;
    await onSubmit();
    if (resolvingSubmitCount === 0) {
      dispatch(increaseResolvingSubmitCount());
    }
    if (resolvingSubmitCount > 0) {
      dispatch(resetResolvingSubmitCount());
    }
  };

  const onSkip = async () => {
    if (!selectedTenancy || !thisQuestionnaire || !currentCase) return;
    const payload: AddSurveyResponsePayload = {
      tenancyId: thisQuestionnaire.tenancyId,
      annotationCaseId: currentCase.caseId,
      status: 'Skipped',
      surveyId: thisQuestionnaire.id,
      questions: [],
    };
    await onSubmitResponse(payload, true);
    questionsForm.reset();
  };

  if (
    (!currentCase && !inPatientDetails) ||
    (selectedTenancy &&
      selectedTenancy?.permissionsSetup &&
      !userHasPermissions(permissionShouldBeChecked))
  ) {
    return null;
  }

  return (
    <div
      className={`d-flex flex-column py-3 ${className ? ' ' + className : ''}`}
    >
      {currentCase && (
        <span className="font-weight-600 font-12 height-5-percent">
          Your {currentCase.eventType} feedback
        </span>
      )}
      <QuestionsForm
        form={questionsForm}
        thisQuestionnaire={thisQuestionnaire}
      />
      <div className="d-flex justify-content-end align-items-center gap-3 height-10-percent">
        {!isSideBar ? (
          <CareMapprButton variant="secondary" onClick={onSkip}>
            Skip
          </CareMapprButton>
        ) : null}
        <CareMapprButton
          onClick={
            discrepency
              ? onSubmitDescrepency
              : isSideBar
                ? onSubmitSideBar
                : onSubmit
          }
        >
          Submit
        </CareMapprButton>
      </div>
    </div>
  );
};

export default QuestionsSidebar;
